/**
 * SA-717 (email triage spec T29): the Mailboxes router editor's model. The
 * editor's state IS the routing policy document, as the server stores it:
 * every control edits one leaf through an updater here, and the Raw view
 * edits the same object, so what is saved is what was loaded plus the
 * person's edits. Pure: no React, no store, no fetch.
 *
 * `locatePolicyError` places the validator's one sentence (a 400's detail)
 * on the form. It reads the sentence's stable prefix; both suites pin
 * sentence and location against `shared/routing-policy-error-cases.json`.
 */
import { OUTCOME_CHOICES } from '@/components/process/stepLiveText'
import { handlerNotOffered, builtinHandlerNotOffered, ruleLeftOut, kindMissingFields, kindTaken, kindIsBuiltin } from './routerPolicyText'

export type PolicyDoc = Record<string, unknown>

export interface KindDoc {
  label: string
  description: string
  handler: string
  handler_config: Record<string, unknown>
  min_confidence?: number
  approve: boolean | null
  status: string
  reply_kind?: string | null
}

export interface RuleDoc {
  id: string
  kind: string
  confidence: number
  when: Record<string, unknown>
}

export interface ModelDoc { enabled: boolean; instruction: string }

export const STATUSES = ['active', 'waiting', 'resolved'] as const
export const BUILTIN_IDS = ['internal', 'automated'] as const

/** `routing_config.BUILTIN_DEFAULTS`, the parts the editor shows. */
const BUILTIN_DEFAULTS: Partial<KindDoc> = {
  handler: 'none', handler_config: {}, min_confidence: 0.8, approve: false, status: 'resolved',
}

export const EMPTY_POLICY: PolicyDoc = {
  approval_default: true, kinds: {}, builtin: {}, rules: [], model: { enabled: true, instruction: '' },
}

const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}

/** `changes` over `base`; a key set to `undefined` is deleted. */
function patch<T extends object>(base: T, changes: Partial<T>): T {
  const out = { ...base } as Record<string, unknown>
  for (const [key, value] of Object.entries(changes)) {
    if (value === undefined) delete out[key]
    else out[key] = value
  }
  return out as T
}

// ── readers ─────────────────────────────────────────────────────────────────

const isObject = (value: unknown): boolean => !!value && typeof value === 'object' && !Array.isArray(value)

export function kindsOf(doc: PolicyDoc): Record<string, KindDoc> {
  // A null or non-object entry (half-typed YAML) reads as an empty one.
  const kinds = record(doc.kinds)
  if (Object.values(kinds).every(isObject)) return kinds as Record<string, KindDoc>
  return Object.fromEntries(Object.entries(kinds).map(([id, k]) => [id, isObject(k) ? k : {}])) as Record<string, KindDoc>
}

export function builtinOf(doc: PolicyDoc, id: string): Partial<KindDoc> {
  return { ...BUILTIN_DEFAULTS, ...(record(record(doc.builtin)[id]) as Partial<KindDoc>) }
}

export function rulesOf(doc: PolicyDoc): RuleDoc[] {
  if (!Array.isArray(doc.rules)) return []
  // A null or non-object entry (half-typed YAML) reads as an empty rule.
  return doc.rules.every(isObject) ? (doc.rules as RuleDoc[]) : doc.rules.map((r) => (isObject(r) ? r : {})) as RuleDoc[]
}

export function modelOf(doc: PolicyDoc): ModelDoc {
  const model = record(doc.model)
  return { enabled: model.enabled !== false, instruction: typeof model.instruction === 'string' ? model.instruction : '' }
}

export function kindCount(doc: PolicyDoc | null | undefined): number {
  return Object.keys(kindsOf(doc ?? {})).length
}

// ── the channel (SA-947, spec P13, P14) ─────────────────────────────────────

/** The channel a router mailbox's messages carry. */
export type PolicyChannel = 'email' | 'whatsapp-group'

export type RuleField =
  | 'from' | 'from_domain' | 'subject' | 'thread' | 'auto_submitted'
  | 'attachment_mime' | 'attachment_count' | 'attachment_filename'
  | 'sender' | 'body' | 'has_attachments'

const EMAIL_RULE_FIELDS: readonly RuleField[] = [
  'from', 'from_domain', 'subject', 'thread', 'auto_submitted',
  'attachment_mime', 'attachment_count', 'attachment_filename',
]
const GROUP_RULE_FIELDS: readonly RuleField[] = [
  'sender', 'body', 'attachment_mime', 'attachment_count', 'attachment_filename', 'has_attachments', 'thread',
]

/** The rule fields the form shows, in order, for a channel. */
export function ruleFieldsFor(channel: PolicyChannel): readonly RuleField[] {
  return channel === 'whatsapp-group' ? GROUP_RULE_FIELDS : EMAIL_RULE_FIELDS
}

/** Spec W5 (SA-1080 pins it in shared/routing-policy-channel-keys.json): the
 *  conditions a channel's messages never meet, with their form labels. */
export const NEVER_MATCHES: Record<PolicyChannel, Record<string, string>> = {
  'whatsapp-group': {
    from: 'From', from_domain: 'From domain', to: 'To', subject: 'Subject pattern', auto_submitted: 'Auto-submitted',
  },
  email: { sender: 'Sender' },
}

const EMAIL_ONLY_KEYS = NEVER_MATCHES['whatsapp-group']

export function unmatchableKeys(rule: RuleDoc, channel: PolicyChannel): string[] {
  if (channel !== 'whatsapp-group') return []
  const when = record(rule.when)
  return Object.keys(EMAIL_ONLY_KEYS).filter((key) => key in when)
}

export function unmatchableLine(keys: string[]): string | null {
  if (keys.length === 0) return null
  return `Never matches a group message: it names ${keys.map((key) => EMAIL_ONLY_KEYS[key] ?? key).join(', ')}.`
}

/** SA-1080 (spec §5.3): a document written for one channel, made fit for
 *  another. A rule naming a condition the channel never meets is dropped and a
 *  kind whose handler the channel does not take is set to `none`; each is
 *  said in `leftOut`. Raw reads: anything not shaped as expected is kept. */
export function adaptToChannel(
  doc: PolicyDoc, channel: PolicyChannel,
  handlers: Array<{ id: string; label: string; channels?: readonly string[] | null }>,
): { doc: PolicyDoc; leftOut: string[] } {
  const leftOut: string[] = []
  const next: PolicyDoc = { ...doc }
  let changed = false

  if (Array.isArray(doc.rules)) {
    const never = NEVER_MATCHES[channel]
    const kept = doc.rules.filter((rule) => {
      if (!isObject(rule)) return true
      const r = rule as Record<string, unknown>
      if (!isObject(r.when)) return true
      const labels = Object.keys(r.when as object).filter((key) => Object.hasOwn(never, key)).map((key) => never[key])
      if (labels.length === 0) return true
      leftOut.push(ruleLeftOut(String(r.id ?? ''), labels, channel))
      return false
    })
    if (kept.length !== doc.rules.length) { next.rules = kept; changed = true }
  }

  if (isObject(doc.kinds)) {
    const noneLabel = handlers.find((h) => h.id === 'none')?.label ?? 'none'
    const kinds: Record<string, unknown> = { ...(doc.kinds as Record<string, unknown>) }
    let kindsChanged = false
    for (const [id, kind] of Object.entries(kinds)) {
      if (!isObject(kind)) continue
      const handler = handlers.find((h) => h.id === (kind as Record<string, unknown>).handler)
      if (!handler || !handler.channels || handler.channels.includes(channel)) continue
      kinds[id] = { ...(kind as object), handler: 'none', handler_config: {} }
      leftOut.push(handlerNotOffered(id, handler.label, channel, noneLabel))
      kindsChanged = true
    }
    if (kindsChanged) { next.kinds = kinds; changed = true }
  }

  if (isObject(doc.builtin)) {
    const noneLabel = handlers.find((h) => h.id === 'none')?.label ?? 'none'
    const builtin: Record<string, unknown> = { ...(doc.builtin as Record<string, unknown>) }
    let builtinChanged = false
    for (const id of ['internal', 'automated']) {
      const entry = builtin[id]
      if (!Object.hasOwn(builtin, id) || !isObject(entry)) continue
      const handler = handlers.find((h) => h.id === (entry as Record<string, unknown>).handler)
      if (!handler || !handler.channels || handler.channels.includes(channel)) continue
      builtin[id] = { ...(entry as object), handler: 'none', handler_config: {} }
      leftOut.push(builtinHandlerNotOffered(id, handler.label, channel, noneLabel))
      builtinChanged = true
    }
    if (builtinChanged) { next.builtin = builtin; changed = true }
  }

  return changed ? { doc: next, leftOut } : { doc, leftOut }
}

/** Spec W15 (SA-945): the handlers a channel takes, as the server serves
 *  them (`channels`; null or absent = every channel). */
export function handlersFor<T extends { id: string; channels?: readonly string[] | null }>(
  handlers: T[], channel: PolicyChannel,
): T[] {
  return handlers.filter((h) => !h.channels || h.channels.includes(channel))
}

/** SA-945: on a group a message is "known" when it quotes a message the
 *  ledger holds, so the Thread condition's words follow the channel. */
const THREAD_WORDS: Record<PolicyChannel, { new: string; known: string }> = {
  email: { new: 'A new thread', known: 'A known thread' },
  'whatsapp-group': { new: 'Not a reply', known: 'A reply to a message we hold' },
}

export function threadOptionWords(channel: PolicyChannel): { new: string; known: string } {
  return THREAD_WORDS[channel]
}

// ── updaters ────────────────────────────────────────────────────────────────

/** Canonical JSON: object keys sorted at every level, arrays kept in order. */
export function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([key, v]) => [key, canonical(v)]))
  }
  return value
}

/** Whether two documents say the same thing. Key order is not content; the
 * order of a list is (rules run in order, so a moved rule is a change). */
export function isSamePolicy(a: PolicyDoc, b: PolicyDoc): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b))
}

export function setPolicy(doc: PolicyDoc, changes: Record<string, unknown>): PolicyDoc {
  return patch(doc, changes)
}

function freeId(taken: readonly string[], prefix: string): string {
  let n = taken.length + 1
  while (taken.includes(`${prefix}${n}`)) n += 1
  return `${prefix}${n}`
}

/** The server's `BUILTIN_KINDS`: ids a policy's own kinds may never take. */
const RESERVED_KIND_IDS: readonly string[] = ['unknown', 'internal', 'automated']

/** SA-1080 (spec §5.1): add one kind, last, and touch nothing else. Reads the
 *  raw `doc.kinds`, so a half-typed entry is kept as it is. A taken or
 *  built-in id returns the same document. */
export function appendKind(
  doc: PolicyDoc, id: string,
  fields: Pick<KindDoc, 'label' | 'description' | 'handler' | 'handler_config'>,
): PolicyDoc {
  const raw = record(doc.kinds)
  if (Object.hasOwn(raw, id) || RESERVED_KIND_IDS.includes(id)) return doc
  const fresh: KindDoc = {
    label: fields.label, description: fields.description, handler: fields.handler,
    handler_config: fields.handler_config, min_confidence: 0.75, approve: null, status: 'active', reply_kind: null,
  }
  return { ...doc, kinds: { ...raw, [id]: fresh } }
}

/** A kind id from its label: `Cotización (B744)` -> `cotizacion_b744`. */
export function kindIdFromLabel(label: string): string {
  const id = label
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .slice(0, 40)
  return id.replace(/_+$/, '')
}

export function addKindRefusal(
  doc: PolicyDoc, draft: { id: string; label: string; description: string; handler: string },
): string | null {
  if (![draft.id, draft.label, draft.description, draft.handler].every((v) => v.trim() !== '')) return kindMissingFields()
  if (RESERVED_KIND_IDS.includes(draft.id)) return kindIsBuiltin(draft.id)
  if (Object.hasOwn(record(doc.kinds), draft.id)) return kindTaken(draft.id)
  return null
}

export function setKind(doc: PolicyDoc, id: string, changes: Partial<KindDoc>): PolicyDoc {
  const kinds = kindsOf(doc)
  if (!(id in kinds)) return doc
  return { ...doc, kinds: { ...kinds, [id]: patch(kinds[id], changes) } }
}

export function removeKind(doc: PolicyDoc, id: string): PolicyDoc {
  const { [id]: _removed, ...rest } = kindsOf(doc)
  return { ...doc, kinds: rest }
}

/** Rename in place (the kinds keep their order) and follow the id where the
 * document refers to it: other kinds' `reply_kind`, and rules. A taken or
 * empty id changes nothing; the server judges the id's shape. */
export function renameKind(doc: PolicyDoc, from: string, to: string): PolicyDoc {
  const kinds = kindsOf(doc)
  const next = to.trim()
  if (!next || next === from || !(from in kinds) || next in kinds) return doc
  const renamed: Record<string, KindDoc> = {}
  for (const [id, entry] of Object.entries(kinds)) {
    renamed[id === from ? next : id] = entry.reply_kind === from ? { ...entry, reply_kind: next } : entry
  }
  return {
    ...doc,
    kinds: renamed,
    rules: rulesOf(doc).map((rule) => (rule.kind === from ? { ...rule, kind: next } : rule)),
  }
}

/** A built-in is stored as overrides only; `builtinOf` reads the defaults under them. */
export function setBuiltin(doc: PolicyDoc, id: string, changes: Partial<KindDoc>): PolicyDoc {
  const builtin = record(doc.builtin)
  return { ...doc, builtin: { ...builtin, [id]: patch(record(builtin[id]), changes) } }
}

export function addRule(doc: PolicyDoc): PolicyDoc {
  const rules = rulesOf(doc)
  const id = freeId(rules.map((rule) => rule.id), 'rule-')
  return { ...doc, rules: [...rules, { id, kind: 'internal', confidence: 1, when: {} }] }
}

export function setRule(doc: PolicyDoc, index: number, changes: Partial<RuleDoc>): PolicyDoc {
  return { ...doc, rules: rulesOf(doc).map((rule, i) => (i === index ? patch(rule, changes) : rule)) }
}

export function removeRule(doc: PolicyDoc, index: number): PolicyDoc {
  return { ...doc, rules: rulesOf(doc).filter((_rule, i) => i !== index) }
}

/** Rules run in order: the first hard match decides. */
export function moveRule(doc: PolicyDoc, index: number, delta: -1 | 1): PolicyDoc {
  const rules = [...rulesOf(doc)]
  const target = index + delta
  if (index < 0 || index >= rules.length || target < 0 || target >= rules.length) return doc
  ;[rules[index], rules[target]] = [rules[target], rules[index]]
  return { ...doc, rules }
}

const empty = (value: unknown) =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

/** One condition of a rule. An empty value removes the condition. */
export function setWhen(doc: PolicyDoc, index: number, key: string, value: unknown): PolicyDoc {
  const rule = rulesOf(doc)[index]
  if (!rule) return doc
  return setRule(doc, index, { when: patch(record(rule.when), { [key]: empty(value) ? undefined : value }) })
}

/** One part of a rule's attachment condition; an attachment with no part left is removed. */
export function setAttachment(doc: PolicyDoc, index: number, key: 'mime' | 'count' | 'filename', value: unknown): PolicyDoc {
  const rule = rulesOf(doc)[index]
  if (!rule) return doc
  const attachment = patch(record(record(rule.when).attachment), { [key]: empty(value) ? undefined : value })
  return setWhen(doc, index, 'attachment', Object.keys(attachment).length > 0 ? attachment : undefined)
}

export function setModel(doc: PolicyDoc, changes: Partial<ModelDoc>): PolicyDoc {
  return { ...doc, model: patch(record(doc.model), changes) }
}

/** A typed list of addresses or domains: commas or line breaks between entries. */
export function splitList(text: string): string[] {
  return text.split(/[,\n]/).map((entry) => entry.trim()).filter(Boolean)
}

// ── placing a 400 sentence ──────────────────────────────────────────────────

export interface PolicyErrorLocation {
  scope: 'policy' | 'kind' | 'builtin' | 'rule' | 'model'
  id?: string
  index?: number
  field?: string
}

const ENTRY_FIELDS = ['label', 'description', 'handler_config', 'handler', 'min_confidence', 'approve', 'status', 'reply_kind']
const RULE_FIELDS = ['id', 'kind', 'confidence', 'when', 'from_domain', 'from', 'to', 'sender', 'subject', 'body',
  'attachment', 'auto_submitted', 'has_attachments', 'thread']

/** The field a sentence's remainder names: its first word when that is a
 * field; a handler's own complaint about its settings is `handler_config`. */
function fieldOf(rest: string, fields: readonly string[]): string | undefined {
  if (rest.includes('handler_config') || rest.startsWith('the "')) return 'handler_config'
  const word = /^[a-z_]+/.exec(rest)?.[0]
  return word && fields.includes(word) ? word : undefined
}

function located(scope: PolicyErrorLocation['scope'], extra: Partial<PolicyErrorLocation>): PolicyErrorLocation {
  const out: PolicyErrorLocation = { scope }
  if (extra.id !== undefined) out.id = extra.id
  if (extra.index !== undefined) out.index = extra.index
  if (extra.field !== undefined) out.field = extra.field
  return out
}

export function locatePolicyError(sentence: string): PolicyErrorLocation {
  let m: RegExpExecArray | null
  if ((m = /^sink_config\.(kinds|builtin)\.([^.]+)\.handler_config\.workflow_id: /.exec(sentence))) {
    return located(m[1] === 'kinds' ? 'kind' : 'builtin', { id: m[2], field: 'handler_config' })
  }
  if ((m = /^Kind id "(.+?)" must/.exec(sentence))) return located('kind', { id: m[1], field: 'id' })
  if ((m = /^"(.+?)" is a built-in kind/.exec(sentence))) return located('kind', { id: m[1], field: 'id' })
  if ((m = /^Unknown key in built-in kind "(.+?)"/.exec(sentence))) return located('builtin', { id: m[1] })
  if ((m = /^Unknown key in kind "(.+?)"/.exec(sentence))) return located('kind', { id: m[1] })
  if ((m = /^Unknown key in the attachment condition of rule "(.+?)"/.exec(sentence))) return located('rule', { id: m[1], field: 'attachment' })
  if ((m = /^Unknown key in the conditions of rule "(.+?)"/.exec(sentence))) return located('rule', { id: m[1], field: 'when' })
  if ((m = /^Unknown key in rule "(.+?)"/.exec(sentence))) return located('rule', { id: m[1] })
  if (/^Unknown key in model/.test(sentence)) return located('model', {})
  if ((m = /^Built-in kind "(.+?)"(?:: (.*)| .*)$/.exec(sentence))) {
    return located('builtin', { id: m[1], field: m[2] ? fieldOf(m[2], ENTRY_FIELDS) : undefined })
  }
  if ((m = /^Kind "(.+?)"(?:: (.*)| .*)$/.exec(sentence))) {
    return located('kind', { id: m[1], field: m[2] ? fieldOf(m[2], ENTRY_FIELDS) : undefined })
  }
  if ((m = /^Rule "(.+?)"(?:: (.*)| (.*))$/.exec(sentence))) {
    const field = m[2]
      ? (m[2].startsWith('that id') ? 'id' : fieldOf(m[2], RULE_FIELDS))
      : (m[3]?.startsWith('is declared twice') ? 'id' : undefined)
    return located('rule', { id: m[1], field })
  }
  if ((m = /^Rule (\d+)(?:: (.*)| .*)$/.exec(sentence))) {
    return located('rule', { index: Number(m[1]) - 1, field: m[2] ? fieldOf(m[2], RULE_FIELDS) : undefined })
  }
  if ((m = /^model\.(enabled|instruction)\b/.exec(sentence))) return located('model', { field: m[1] })
  if (/^model\b/.test(sentence)) return located('model', {})
  if ((m = /^(approval_default|kinds|rules)\b/.exec(sentence))) return located('policy', { field: m[1] })
  if (/under builtin\.$/.test(sentence)) return located('policy', { field: 'builtin' })
  return located('policy', {})
}

// The fields each part of the form renders a slot under.
const KIND_SLOTS = new Set(['id', 'label', 'description', 'handler', 'handler_config', 'min_confidence', 'approve', 'status', 'reply_kind'])
const BUILTIN_SLOTS = new Set(['handler', 'handler_config', 'min_confidence', 'approve', 'status'])
const RULE_BASE_SLOTS = ['id', 'kind', 'confidence']
const RULE_SLOTS: Record<PolicyChannel, Set<string>> = {
  email: new Set([...RULE_BASE_SLOTS, 'from', 'from_domain', 'subject', 'attachment', 'auto_submitted', 'thread']),
  'whatsapp-group': new Set([...RULE_BASE_SLOTS, 'sender', 'body', 'attachment', 'has_attachments', 'thread']),
}
const MODEL_SLOTS = new Set(['enabled', 'instruction'])

/** Where the form shows a located sentence: under its field when the form
 * renders that field for the channel, else on its entry, else at the top. A
 * sentence is never dropped. */
export function errorSlot(location: PolicyErrorLocation, doc: PolicyDoc, channel: PolicyChannel = 'email'): string {
  const field = location.field ?? ''
  if (location.scope === 'kind' && location.id !== undefined && location.id in kindsOf(doc)) {
    return KIND_SLOTS.has(field) ? `kind:${location.id}:${field}` : `kind:${location.id}`
  }
  if (location.scope === 'builtin' && location.id !== undefined && (BUILTIN_IDS as readonly string[]).includes(location.id)) {
    return BUILTIN_SLOTS.has(field) ? `builtin:${location.id}:${field}` : `builtin:${location.id}`
  }
  if (location.scope === 'rule') {
    const rules = rulesOf(doc)
    const index = location.index ?? rules.findIndex((rule) => rule.id === location.id)
    if (index >= 0 && index < rules.length) return RULE_SLOTS[channel].has(field) ? `rule:${index}:${field}` : `rule:${index}`
  }
  if (location.scope === 'model') return MODEL_SLOTS.has(field) ? `model:${field}` : 'model'
  if (location.scope === 'policy' && field === 'approval_default') return 'policy:approval_default'
  return 'top'
}

/** The outcomes a closing kind may propose: the Outcome control's list, less
 *  "not an enquiry" (that is a rejection, not a close). */
export const CLOSE_OUTCOME_CHOICES: readonly string[] = OUTCOME_CHOICES.filter((code) => code !== 'not_an_enquiry')

/** A closing kind's `handler_config` with `code` added at the end or removed. */
export function toggleOutcome(config: Record<string, unknown>, code: string, on: boolean): { outcomes: string[] } {
  const stored = Array.isArray(config.outcomes) ? config.outcomes.filter((c): c is string => typeof c === 'string') : []
  const rest = stored.filter((c) => c !== code)
  return { outcomes: on ? (stored.includes(code) ? stored : [...rest, code]) : rest }
}
